import React from "react";
import { useAppStore } from "../../state/app-store";
import { filterClass, joinPath } from "../../utils/class-utils";
import { withReturnContext } from "./shared/link-context";
import { CardItemIdRoot } from "./shared/item-id-comment";
import { getTextAlignClass, resolveHorizontalContentAlign } from "../utils/content-alignment";
import type { NewsCardProps } from "./types";

export function NewsCard(props: NewsCardProps) {
  const {
    newsItem,
    basePath = "/news",
    buttonColorClass = "primary",
    paddingClass = "py-0",
    cardBgClass = "text-bg-white",
    cardBorderClass = "no-class",
    cardBorderColorClass = "no-class",
    contentAlignClass = "no-class",
  } = props;
  // Check if we have valid news item data (with non-empty values)
  const hasValidData = newsItem && (
    (newsItem.item_name && newsItem.item_name.trim()) ||
    (newsItem.slug && newsItem.slug.trim()) ||
    (newsItem.teaser && newsItem.teaser.trim()) ||
    (newsItem.content && newsItem.content.trim()) ||
    (newsItem.page_title && newsItem.page_title.trim())
  );
  const item = hasValidData ? newsItem : null;
  const pageId = useAppStore((state) => state.documentId);

  // Detect editor mode to disable links
  const isInEditor = typeof document !== "undefined" && document.body.classList.contains("is-easyblocks-editor");

  /**
   * ===== FRONTEND: CLASSES & STRUCTURE (EDIT HERE) =====
   */
  const buttonVariant = buttonColorClass || "primary";

  const containerClassName = [
    filterClass(paddingClass),
  ]
    .filter(Boolean)
    .join(" ");

  const hasCardBorder = filterClass(cardBorderClass) || filterClass(cardBorderColorClass);
  const resolvedContentAlignClass = getTextAlignClass(
    resolveHorizontalContentAlign(contentAlignClass, undefined, "start")
  );

  const cardClassName = [
    "news-latest-row__card",
    "card",
    "rounded-4",
    "h-100",
    filterClass(cardBgClass),
    hasCardBorder ? "border" : "",
    filterClass(cardBorderClass),
    filterClass(cardBorderColorClass),
  ]
    .filter(Boolean)
    .join(" ");

  const cardBodyClassName = ["news-latest-row__card-body", "card-body", "d-flex", "flex-column", resolvedContentAlignClass]
    .filter(Boolean)
    .join(" ");
  const placeholderCardStyle = { "--bs-bg-opacity": ".9" } as React.CSSProperties;

  const classes = {
    header: "news-latest-row__header d-flex flex-column gap-4",
    title: "news-latest-row__title h6 mb-1",
    date: "news-latest-row__date small fw-normal",
    teaser: "news-latest-row__teaser card-text mb-0",
    cta: "news-latest-row__cta mt-3",
    ctaLink: ["news-latest-row__cta-link", "btn", "btn-sm", `btn-${buttonVariant}`]
      .filter(Boolean)
      .join(" "),
    ctaDisabled: "news-latest-row__cta-disabled btn btn-sm btn-outline-secondary",
    emptyText: "small",
  };

  if (!item) {
    // In live view, hide the component if there's no item
    if (!isInEditor) {
      return null;
    }
    // In edit mode, show a message
    return (
      <div className={containerClassName}>
        <div className={cardClassName} style={placeholderCardStyle}>
          <div className={cardBodyClassName}>
            <div className={classes.emptyText}>No news item selected. Pick a news item to preview.</div>
          </div>
        </div>
      </div>
    );
  }

  const title = item.item_name ?? "(Untitled news item)";

  const href = item.slug ? withReturnContext(joinPath(basePath, item.slug), pageId) : undefined;

  return (
    <div className={containerClassName}>
      <CardItemIdRoot itemId={item.item_id} className={cardClassName}>
        <div className={cardBodyClassName}>
          <div className={classes.header}>
            <div>
              <div className={classes.title}>{title}</div>
              {item.date_posted ? <div className={classes.date}>Date Posted: {item.date_posted}</div> : null}
            </div>
            {item.teaser ? (
              <div className={classes.teaser} dangerouslySetInnerHTML={{ __html: item.teaser }} />
            ) : (
              <div className={classes.teaser}>
                <em>No teaser</em>
              </div>
            )}
          </div>

          <div className={classes.cta}>
            {href ? (
              <a
                className={classes.ctaLink}
                href={href}
                onClick={(e) => {
                  if (isInEditor) {
                    e.preventDefault();
                  }
                }}
              >
                Read more
              </a>
            ) : (
              <button className={classes.ctaDisabled} type="button" disabled>
                Missing slug
              </button>
            )}
          </div>
        </div>
      </CardItemIdRoot>
    </div>
  );
}


